import type { DBAccount } from "@/local-shared/model";
import { SUCCESS, type SocialAPI } from ".";
import HTTPStatus from "@/realize-shared/HTTPStatus";

const TWITCH_CLIENT_ID: string = import.meta.env.VITE_TWITCH_CLIENT_ID as string;

interface TwitchChannelInfo
{
    id: string;
    login: string;
    display_name: string;
    type: "admin" | "global_mod" | "staff" | "";
    broadcaster_type: "affiliate" | "partner" | "";
    description: string;
    profile_image_url: string;
    offline_image_url: string;
    view_count: number;
    email: string;
    created_at: string;
}

export default class TwitchAPI
{
    public constructor(private api: SocialAPI)
    {
        Logger.log(this.api);
    }


    private async validateAccessToken(): Promise<boolean|string>
    {
        try
        {
            const response = await this.api.fetch<DBAccount>("/twitch/validate", {
                method: "GET",
            });

            Logger.log(response);

            if (!response.status || response.status !== SUCCESS)
            {
                Logger.error(
                    "Twitch API",
                    `Failed to validate access token: ${response.status} ${response.message}`
                );

                return false;
            }

            Logger.log("Twitch API", "Access token is valid.");


            return response.data?.connection_tokens.access_token ?? false;
        }
        catch (error)
        {
            Logger.error("Twitch API", "Error validating access token: ", error);

            return false;
        }
    }

    public async getChannelInfo(userId: string): Promise<{
        broadcaster_type: "affiliate" | "partner" | "";
        id: string;
    }>
    {
        const accessToken = await this.validateAccessToken();

        if (!accessToken)
        {
            throw new Error("Invalid access token.");
        }

        const response = await fetch(`https://api.twitch.tv/helix/users?id=${userId}`, {
            headers: {
                "Client-ID": TWITCH_CLIENT_ID,
                "Authorization": `Bearer ${accessToken}`
            }
        });

        if (!response.ok)
        {
            throw new Error(`Failed to fetch channel info: ${response.status} ${response.statusText}`);
        }

        if (response.status as HTTPStatus !== SUCCESS)
        {
            throw new Error(`Unexpected response status: ${response.status}`);
        }

        // Log the response for debugging purpose
        const json: {
            data: TwitchChannelInfo[]
        } = await response.json() as {
            data: TwitchChannelInfo[]
        };

        if (json.data.length === 0)
        {
            throw new Error("No channel data found for the provided user ID.");
        }

        Logger.log("Twitch Channel Info Response", json);

        return {
            broadcaster_type: json.data[0].broadcaster_type,
            id: json.data[0].id,
        };
    }
}
